<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span class="headline">{{ titleText }}</span>
      <v-spacer/>
      <Icon
        :small="false"
        :large="true"
        icon="mdi-close"
        icon-color="tertiary"
        dataTestId="cruise-close"
        :tooltipText="$t('close')"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>
    <v-card-text class="mt-6">
      <FormWrapper
        ref="form"
        formRef="cruiseDataForm"
        :lazyValidation="true"
        :buttonText="editing ? $t('updateCruiseData') : $t('createCruiseData')"
        @submit="saveChangesPressed"
      >
        <v-container fluid>
          <v-row>
            <v-col cols="12" md="6">
              <ProductAutocomplete
              v-if="!isVolumeBased"
              :woodType="woodType"
              :productId="editing ? propCruiseData.productId : undefined"
              :excludedProducts="excludedProducts"
              ref="firstField"
              @product-chosen="productChosen"
              />
               <v-select v-else
                v-model="selectedSpecies"
                :items="filteredSpecies"
                :label="$t('species')"
                :rules="[rules.required]"
                ref="firstField"
                item-text="commonName"
                return-object
                text="commonName"/>
            </v-col>
            <v-col cols="12" md="6">
              <MoneyTextField
              data-testid="cruise-price"
              :initialValue="cruiseData.pricePerUnit"
              :label="labels.price"
              @val-changed="pricePerUnitEntered"/>
            </v-col>
            <v-col cols="12" :md="bottomRowColumns">
              <v-text-field
                v-model="adjustmentValue"
                data-testid="cruise-harvested"
                @blur="setFieldValue(cruiseData.adjustmentTons, 'harvested')"
                :label="labels.harvestAdjustment"
                :rules="[rules.positiveDecimal, rules.validHarvestedValue(maxCruiseValue)]"
                color="black"
                type="number"
                hide-spin-buttons
              ></v-text-field>
            </v-col>
            <v-col cols="12" :md="bottomRowColumns">
              <v-text-field
                data-testid="cruise-expected"
                v-model="expectedValue"
                :label="labels.expected"
                @blur="setFieldValue(expectedValue, 'expected')"
                :rules="[rules.positiveDecimal, rules.validExpectedValue(maxCruiseValue)]"
                color="black"
                type="number"
                hide-spin-buttons
              ></v-text-field>
            </v-col>
            <v-col cols="12" :md="bottomRowColumns" v-if="obligation">
              <MoneyTextField
              :initialValue="cruiseData.obligationRate"
              :label="$t('obligationRate')"
              @val-changed="cruiseData.obligationRate = $event"/>
            </v-col>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import FieldRules, { MAX_TRACT_CONTENT_QUANTITY } from '@/utils/rules.js'
import { mapActions, mapGetters } from 'vuex'
import { TractContent } from '@/model/Tract.js'
import { EnumValue, TractTypeSpecialization } from '@/utils/Enumerations'
import { NumericConstants } from '@/utils/constants'

export default {
  name: 'CruiseDataForm',

  props: {
    propCruiseData: {
      type: TractContent,
      default: null
    },
    tract: Object,
    editing: { Boolean, default: false },
    hideTitle: { Boolean, default: false },
    isVolumeBased: { Boolean, default: false },
    woodType: {
      type: EnumValue,
      required: false,
      default: undefined
    }
  },

  components: {
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    ProductAutocomplete: () => import('@/components/autocomplete/ProductAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.firstField.$children[0].focus()
    }, 0)
  },

  data: () => ({
    TractTypeSpecialization,
    cruiseData: {
      expectedTons: 0,
      adjustmentTons: 0,
      adjustment: 0,
      expected: 0,
      obligationRate: null
    },
    selectedSpecies: null,
    excludedSpecies: [],
    rules: FieldRules.rules
  }),

  computed: {
    ...mapGetters('tract-contents', ['allTractContents']),
    ...mapGetters('product', ['allProducts']),
    ...mapGetters('species', ['allSpecies']),
    titleText () {
      return this.editing ? this.$t('editCruiseData') : this.$t('createCruiseData')
    },

    filteredSpecies () {
      return this.allSpecies.filter(s => s.speciesId === this.propCruiseData?.speciesId || !this.allTractContents.some(tc => tc.speciesId === s.speciesId))
    },

    labels () {
      return {
        price: this.isVolumeBased ? this.$t('pricePerMBF') : this.$t('pricePerTon'),
        harvestAdjustment: this.isVolumeBased ? this.$t('manualHarvestAdjustmentMBF') : this.$t('manualHarvestAdjustment'),
        expected: this.isVolumeBased ? this.$t('expectedMBF') : this.$t('expected')
      }
    },

    maxCruiseValue () {
      return MAX_TRACT_CONTENT_QUANTITY / (this.isVolumeBased ? NumericConstants.MbfDivisor : NumericConstants.ShortTonDivisor)
    },

    adjustmentValue: {
      get () {
        return this.isVolumeBased ? this.cruiseData.adjustment : this.cruiseData.adjustmentTons
      },
      set (val) {
        if (this.isVolumeBased) {
          this.cruiseData.adjustment = val
        } else {
          this.cruiseData.adjustmentTons = val
        }
      }
    },
    expectedValue: {
      get () {
        return this.isVolumeBased ? this.cruiseData.expected : this.cruiseData.expectedTons
      },
      set (val) {
        if (this.isVolumeBased) {
          this.cruiseData.expected = val
        } else {
          this.cruiseData.expectedTons = val
        }
      }
    },

    excludedProducts () {
      return [
        ...this.allTractContents.map(tc => tc.product),
        ...this.allProducts.filter(p => p.isScalable).map(p => p.name)
      ]
    },

    obligation () {
      return this.tract.type.specialization === TractTypeSpecialization.ObligationUncapped.value && this.isVolumeBased
    },

    bottomRowColumns () {
      return this.obligation ? 4 : 6
    }
  },

  async created () {
    if (this.editing) {
      this.cruiseData = this.propCruiseData.clone()

      if (this.isVolumeBased) {
        this.cruiseData.expected = (this.propCruiseData?.expected ?? 0) / NumericConstants.MbfDivisor
        this.cruiseData.adjustment = (this.propCruiseData?.adjustment ?? 0) / NumericConstants.MbfDivisor
      }
    }

    if (this.isVolumeBased) {
      await this.fetchSpecies()
      this.selectedSpecies = this.allSpecies.find(s => s.speciesId === this.propCruiseData?.speciesId)
    }
  },

  methods: {
    ...mapActions('tract-contents', [
      'createTractContent',
      'updateTractContent'
    ]),
    ...mapActions('species', ['fetchSpecies']),
    ...mapActions('product', ['fetchProducts']),
    productChosen (product) { this.cruiseData.productId = product.productId },
    pricePerUnitEntered (amount) { this.cruiseData.pricePerUnit = amount },

    saveChangesPressed () {
      if (!this.$refs.form.$refs.cruiseDataForm.validate()) {
        return
      }

      const cruiseDataRequestObject = this.createRequestObject(this.cruiseData)

      if (this.editing) {
        cruiseDataRequestObject.tractContentId = this.propCruiseData.tractContentId
        this.editCruiseData(cruiseDataRequestObject)
      } else {
        this.createCruiseData(cruiseDataRequestObject)
      }
    },

    setFieldValue (value, type) {
      if (value === undefined) {
        if (type === 'expected') {
          this.cruiseData.expectedTons = 0
        } else {
          this.cruiseData.adjustmentTons = 0
        }
        return
      }
      if (value.length === 0) {
        if (type === 'expected') {
          this.cruiseData.expectedTons = 0
        } else {
          this.cruiseData.adjustmentTons = 0
        }
      }
    },

    createRequestObject (cruiseData) {
      const factor = this.isVolumeBased ? NumericConstants.MbfDivisor : NumericConstants.ShortTonDivisor

      const expected = Math.round((this.isVolumeBased ? this.cruiseData.expected : this.cruiseData.expectedTons) * factor)
      const adjustment = Math.round((this.isVolumeBased ? this.cruiseData.adjustment : this.cruiseData.adjustmentTons) * factor)

      return {
        productId: this.isVolumeBased ? null : cruiseData.productId,
        speciesId: this.isVolumeBased ? this.selectedSpecies.speciesId : null,
        tractId: this.tract.tractId,
        expected,
        adjustment,
        pricePerUnit: cruiseData.pricePerUnit,
        obligationRate: cruiseData.obligationRate
      }
    },

    async editCruiseData (cruiseDataRequestObject) {
      await this.updateTractContent(cruiseDataRequestObject)
      const adjustmentDelta = cruiseDataRequestObject.adjustment - (this.propCruiseData.adjustment ?? 0)
      this.$emit('cruise-data-changed', this.obligationAccrualPrompt(cruiseDataRequestObject, this.propCruiseData.tractContentId, adjustmentDelta))
    },

    async createCruiseData (cruiseDataRequestObject) {
      const response = await this.createTractContent(cruiseDataRequestObject)
      this.$emit('cruise-data-changed', this.obligationAccrualPrompt(cruiseDataRequestObject, response.data.tractContentId, cruiseDataRequestObject.adjustment))
    },

    obligationAccrualPrompt (cruiseDataRequestObject, tractContentId, adjustmentDelta) {
      if (!this.obligation || !adjustmentDelta || !cruiseDataRequestObject.obligationRate) return undefined
      return { tractContentId }
    }
  }
}
</script>
